/**
 * 编程题 HTML 清洗工具
 *
 * 仅针对 Scratch/Python/C++ 编程题的 content、referenceAnswer、scoringPoints 字段执行清洗。
 * 客观题不受影响。
 *
 * 核心策略：彻底移除所有 <p>、<div>、<span> 容器标签，只保留纯文本和 <img> 标签，
 * 让文字和图片直接相邻排列，与单选题渲染效果一致。
 */

import { isCodingQuestion } from '../types/question';

/**
 * 对编程题的 HTML 字段执行清洗
 * @param html - 原始 HTML 字符串
 * @returns 清洗后的 HTML 字符串
 */
export function cleanCodingQuestionHtml(html: string): string {
  if (!html) return html;

  let result = html;

  // ========== 第一阶段：解码 HTML 实体 ==========
  result = result.replace(/&lt;/g, '<');
  result = result.replace(/&gt;/g, '>');
  result = result.replace(/&quot;/g, '"');
  result = result.replace(/&#10;/g, '\n');
  result = result.replace(/&#13;/g, '\r');
  result = result.replace(/&amp;/g, '&');

  // ========== 第二阶段：移除所有空白容器 ==========
  // 移除空的 <p>、<div>、<span> 标签（包括只含空白字符、&nbsp;、<br> 的情况）
  result = result.replace(/<p[^>]*>(\s|&nbsp;|<br\s*\/?>|\n|\r)*<\/p>/gi, '');
  result = result.replace(/<div[^>]*>(\s|&nbsp;|<br\s*\/?>|\n|\r)*<\/div>/gi, '');
  result = result.replace(/<span[^>]*>(\s|&nbsp;|<br\s*\/?>|\n|\r)*<\/span>/gi, '');

  // ========== 第三阶段：统一块级结构为 <p>，消除 <br> 与 <p> 混用 ==========
  // 目标：所有块级分隔只用 <p> 标签（CSS .question-html-content p { margin-bottom: 0.5em }）
  // 彻底移除 <br>，避免 <br> 在 line-height:1.75 下产生完整行高，与 <p> 混用会出现双重空行

  // 将 <span>...</span> 替换为内容（行内元素，不加任何分隔）
  result = result.replace(/<span[^>]*>([\s\S]*?)<\/span>/gi, '$1');

  // 将 <p>...</p> 规范化：移除属性、清理内部 \n 和多余空白
  result = result.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (_match, content) => {
    const trimmed = content.replace(/\n/g, '').replace(/\s+/g, ' ').trim();
    if (!trimmed) return '';
    return `<p>${trimmed}</p>`;
  });

  // 将 <div>...</div> 转为 <p>...</p>（块级 → 段落）
  result = result.replace(/<div[^>]*>([\s\S]*?)<\/div>/gi, (_match, content) => {
    const trimmed = content.replace(/\n/g, '').replace(/\s+/g, ' ').trim();
    if (!trimmed) return '';
    return `<p>${trimmed}</p>`;
  });

  // ========== 第三阶段补充：把 <br> 分隔的文本切分为多个 <p> ==========
  // 关键：彻底消除 <br>，让所有换行都通过 <p> 的 margin-bottom 实现
  // 先把所有 <br> 统一为 \n 作为分隔符
  result = result.replace(/<br\s*\/?>/gi, '\n');

  // ========== 第三阶段补充2：图片独立成块 ==========
  // 把 <img> 标签前后插入 \n，让图片成为独立的"段落"
  // 相邻图片之间用空格分隔（保持同行排列），不换行
  result = result.replace(/<img[^>]*>/gi, (match) => `\n${match}\n`);
  // 相邻图片：消除两张图片之间的 \n（保留空格让它们并排）
  result = result.replace(/(<img[^>]*>)\s*\n\s*(<img)/gi, '$1 $2');

  // ========== 第三阶段补充3：按 \n 切分，每段包裹 <p> ==========
  // 此时 result 中只剩纯文本、<img>、\n 三种元素
  // 把每一段（文本段或单张/多张图片段）都包裹进 <p>
  if (result) {
    const segments = result.split(/\n+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
    if (segments.length > 0) {
      result = segments.map(s => `<p>${s}</p>`).join('');
    }
  }

  // ========== 第四阶段：清理残留 ==========
  // 移除 <p> 内部嵌套的空 <p>（防御性处理）
  result = result.replace(/<p>\s*<\/p>/gi, '');
  // 合并相邻的 <p> 标签间空白
  result = result.replace(/<\/p>\s+<p>/gi, '</p><p>');

  // ========== 第八阶段：序号自动强制分行 ==========
  // 按 </p><p> 分割成段落，在每个段落内独立检测序号模式
  // 检测到序号时，把当前段落从序号位置切分为两个 <p>（实现强制分行）
  const regions = result.split(/<\/p><p>/);

  const processedRegions = regions.map(region => {
    // 预处理：收集所有 <img> 标签的位置范围，避免在标签内部插入分隔
    const imgRanges: Array<{ start: number; end: number }> = [];
    const imgTagPattern = /<img[^>]*>/gi;
    let imgMatch: RegExpExecArray | null;
    while ((imgMatch = imgTagPattern.exec(region)) !== null) {
      imgRanges.push({ start: imgMatch.index, end: imgMatch.index + imgMatch[0].length });
    }

    // 辅助函数：检查位置是否在 <img> 标签内部
    const isInsideImgTag = (pos: number): boolean => {
      return imgRanges.some(range => pos >= range.start && pos < range.end);
    };

    // 收集候选序号位置
    const candidates: Array<{ pos: number; num: number; type: 'dot' | 'paren' }> = [];
    const excludedPositions = new Set<number>(); // 题号位置
    const protectedDecimals = new Set<number>(); // 小数位置

    // 1. 扫描所有 "数字." 格式
    const dotPattern = /(\d+)\./g;
    let match: RegExpExecArray | null;
    while ((match = dotPattern.exec(region)) !== null) {
      const num = parseInt(match[1], 10);
      const pos = match.index;

      // 跳过 <img> 标签内部的匹配（避免破坏图片 URL）
      if (isInsideImgTag(pos)) {
        continue;
      }

      // 检查是否为小数（后面紧跟数字）
      const nextChar = region[pos + match[0].length];
      if (nextChar && /\d/.test(nextChar)) {
        protectedDecimals.add(pos);
        continue;
      }

      candidates.push({ pos, num, type: 'dot' });
    }

    // 2. 扫描所有 "（数字）" 格式
    const parenPattern = /（(\d+)）/g;
    while ((match = parenPattern.exec(region)) !== null) {
      const num = parseInt(match[1], 10);
      const pos = match.index;

      // 跳过 <img> 标签内部的匹配
      if (isInsideImgTag(pos)) {
        continue;
      }

      candidates.push({ pos, num, type: 'paren' });
    }

    // 3. 识别题号（连续递增的大数字点序号）
    // 仅当数字 ≥ 10 时才考虑为题号候选
    for (let i = 0; i < candidates.length - 1; i++) {
      if (candidates[i].type !== 'dot') continue;
      if (candidates[i].num < 10) continue;

      for (let j = i + 1; j < Math.min(i + 5, candidates.length); j++) {
        if (candidates[j].type !== 'dot') continue;
        if (candidates[j].num < 10) continue;

        const gap = candidates[j].pos - candidates[i].pos;
        const numDiff = candidates[j].num - candidates[i].num;

        // 如果数字差为1且间距合理（50字符内），认为是题号序列
        if (numDiff === 1 && gap > 0 && gap < 50) {
          excludedPositions.add(candidates[i].pos);
          excludedPositions.add(candidates[j].pos);
        }
      }
    }

    // 4. 在段落内按位置顺序处理候选序号
    // 检测到序号时，在序号前插入 </p><p> 实现段落切分（替代原来的 <br>）
    let lastMajorNum = 0;
    let lastMinorNum = 0;
    let offset = 0;
    const SPLIT_TOKEN = '</p><p>';
    const SPLIT_LEN = SPLIT_TOKEN.length;

    const sortedCandidates = candidates.sort((a, b) => a.pos - b.pos);

    for (const candidate of sortedCandidates) {
      if (excludedPositions.has(candidate.pos) || protectedDecimals.has(candidate.pos)) {
        continue;
      }

      if (candidate.type === 'dot') {
        if (candidate.num > lastMajorNum) {
          const targetStr = `${candidate.num}.`;
          const adjustedPos = candidate.pos + offset;
          const searchStart = Math.max(0, adjustedPos - 10);
          const insertPos = region.indexOf(targetStr, searchStart);
          if (insertPos >= 0) {
            const beforeInsert = region.substring(Math.max(0, insertPos - SPLIT_LEN), insertPos);
            if (!beforeInsert.endsWith(SPLIT_TOKEN)) {
              region = region.substring(0, insertPos) + SPLIT_TOKEN + region.substring(insertPos);
              offset += SPLIT_LEN;
            }
            lastMajorNum = candidate.num;
            lastMinorNum = 0;
          }
        }
      } else if (candidate.type === 'paren') {
        if (candidate.num > lastMinorNum) {
          const targetStr = `（${candidate.num}）`;
          const adjustedPos = candidate.pos + offset;
          const searchStart = Math.max(0, adjustedPos - 10);
          const insertPos = region.indexOf(targetStr, searchStart);
          if (insertPos >= 0) {
            const beforeInsert = region.substring(Math.max(0, insertPos - SPLIT_LEN), insertPos);
            if (!beforeInsert.endsWith(SPLIT_TOKEN)) {
              region = region.substring(0, insertPos) + SPLIT_TOKEN + region.substring(insertPos);
              offset += SPLIT_LEN;
            }
            lastMinorNum = candidate.num;
          }
        }
      }
    }

    return region;
  });

  result = processedRegions.join('</p><p>');

  // 最终清理：移除空段落、合并相邻 <p> 间空白
  result = result.replace(/<p>\s*<\/p>/gi, '');
  result = result.replace(/<\/p>\s+<p>/gi, '</p><p>');

  return result;
}

/**
 * 判断是否需要对指定题型执行编程题 HTML 清洗
 * 仅 scratch_coding / python_coding / cpp_coding 需要清洗
 */
export function shouldCleanCodingHtml(questionType: string): boolean {
  return isCodingQuestion(questionType);
}

/**
 * 编辑器模式专用 HTML 规范化函数
 * 执行完整的清洗流程（前7阶段）+ 序号自动分行（第8阶段）
 * 确保编辑器中显示的格式与预览模式一致
 */
export function normalizeEditorHtml(html: string): string {
  if (!html) return html;

  // 先执行完整的清洗流程（包含所有8个阶段）
  return cleanCodingQuestionHtml(html);
}

/**
 * 仅执行序号自动分行逻辑（不执行其他清洗步骤）
 * 用于编辑器加载数据时对 content 字段预处理
 *
 * 支持的序号格式：
 * - 数字+点：`1.` `2.` `3.`
 * - 数字+右括号：`1)` `2)` `3)`
 * - 中文括号数字：`（1）` `（2）` `（3）`
 * - 圆圈数字：`①` `②` `③` ... `⑳`
 * - 中文数字+顿号：`一、` `二、` `三、` ... `十、`
 * - 字母+点：`A.` `B.` `C.`（仅当连续出现≥2个才识别）
 *
 * 幂等性保证：
 * - 已分行的序号不会再次分行（检测到 `<br>` 或 `</p>` 时不插入）
 * - 多次执行结果一致
 */
export function autoSplitNumbers(html: string): string {
  if (!html) return html;

  let result = html;

  // 预处理：收集所有 <img> 标签的位置范围，避免在标签内部插入 <br>
  const imgRanges: Array<{ start: number; end: number }> = [];
  const imgTagPattern = /<img[^>]*>/gi;
  let imgMatch: RegExpExecArray | null;
  while ((imgMatch = imgTagPattern.exec(result)) !== null) {
    imgRanges.push({ start: imgMatch.index, end: imgMatch.index + imgMatch[0].length });
  }

  const isInsideImgTag = (pos: number): boolean => {
    return imgRanges.some(range => pos >= range.start && pos < range.end);
  };

  // 收集候选序号位置
  type CandidateType = 'dot' | 'paren' | 'rightParen' | 'circle' | 'chinese' | 'letter';
  const candidates: Array<{ pos: number; num: number; type: CandidateType; raw: string }> = [];
  const excludedPositions = new Set<number>();
  const protectedDecimals = new Set<number>();

  // 中文数字映射（支持 1-20）
  const chineseNumMap: Record<string, number> = {
    '一': 1, '二': 2, '三': 3, '四': 4, '五': 5,
    '六': 6, '七': 7, '八': 8, '九': 9, '十': 10,
    '十一': 11, '十二': 12, '十三': 13, '十四': 14, '十五': 15,
    '十六': 16, '十七': 17, '十八': 18, '十九': 19, '二十': 20
  };

  // 圆圈数字映射（①-⑳ = U+2460 - U+2473）
  const circleNumMap: Record<string, number> = {};
  for (let i = 0; i < 20; i++) {
    circleNumMap[String.fromCharCode(0x2460 + i)] = i + 1;
  }

  let match: RegExpExecArray | null;

  // 1. 扫描所有 "数字." 格式
  const dotPattern = /(\d+)\./g;
  while ((match = dotPattern.exec(result)) !== null) {
    const num = parseInt(match[1], 10);
    const pos = match.index;

    if (isInsideImgTag(pos)) continue;

    const nextChar = result[pos + match[0].length];
    if (nextChar && /\d/.test(nextChar)) {
      protectedDecimals.add(pos);
      continue;
    }

    candidates.push({ pos, num, type: 'dot', raw: match[0] });
  }

  // 2. 扫描所有 "（数字）" 格式（中文括号）
  const parenPattern = /（(\d+)）/g;
  while ((match = parenPattern.exec(result)) !== null) {
    const num = parseInt(match[1], 10);
    const pos = match.index;

    if (isInsideImgTag(pos)) continue;

    candidates.push({ pos, num, type: 'paren', raw: match[0] });
  }

  // 3. 扫描所有 "数字)" 格式（右括号）
  const rightParenPattern = /(\d+)\)/g;
  while ((match = rightParenPattern.exec(result)) !== null) {
    const num = parseInt(match[1], 10);
    const pos = match.index;

    if (isInsideImgTag(pos)) continue;

    // 排除小数（如 3.14) 中的 14)）
    const prevChar = result[pos - 1];
    if (prevChar === '.') continue;

    candidates.push({ pos, num, type: 'rightParen', raw: match[0] });
  }

  // 4. 扫描所有圆圈数字 ① ② ③ ...
  const circlePattern = /[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳]/g;
  while ((match = circlePattern.exec(result)) !== null) {
    const num = circleNumMap[match[0]];
    if (!num) continue;

    const pos = match.index;
    if (isInsideImgTag(pos)) continue;

    candidates.push({ pos, num, type: 'circle', raw: match[0] });
  }

  // 5. 扫描所有中文数字+顿号 "一、" "二、" ...
  const chinesePattern = /(一|二|三|四|五|六|七|八|九|十|十一|十二|十三|十四|十五|十六|十七|十八|十九|二十)、/g;
  while ((match = chinesePattern.exec(result)) !== null) {
    const num = chineseNumMap[match[1]];
    if (!num) continue;

    const pos = match.index;
    if (isInsideImgTag(pos)) continue;

    candidates.push({ pos, num, type: 'chinese', raw: match[0] });
  }

  // 6. 扫描所有字母+点 "A." "B." ...（仅当连续出现≥2个才视为序号）
  const letterPattern = /([A-Z])\./g;
  const letterMatches: Array<{ pos: number; letter: string; num: number }> = [];
  while ((match = letterPattern.exec(result)) !== null) {
    const pos = match.index;
    if (isInsideImgTag(pos)) continue;
    letterMatches.push({ pos, letter: match[1], num: match[1].charCodeAt(0) - 'A'.charCodeAt(0) + 1 });
  }
  // 字母序号需连续出现≥2个才识别（避免误判句末 "A."）
  for (let i = 0; i < letterMatches.length - 1; i++) {
    const cur = letterMatches[i];
    const next = letterMatches[i + 1];
    // 连续字母（A→B→C...）且间距合理（<200字符）
    if (next.num - cur.num === 1 && next.pos - cur.pos < 200) {
      candidates.push({ pos: cur.pos, num: cur.num, type: 'letter', raw: `${cur.letter}.` });
      // 最后一个也要加入
      if (i === letterMatches.length - 2) {
        candidates.push({ pos: next.pos, num: next.num, type: 'letter', raw: `${next.letter}.` });
      }
    }
  }

  // 7. 识别题号（连续递增的大数字点序号，仅≥10的数字才考虑为题号）
  for (let i = 0; i < candidates.length - 1; i++) {
    if (candidates[i].type !== 'dot') continue;
    if (candidates[i].num < 10) continue;

    for (let j = i + 1; j < Math.min(i + 5, candidates.length); j++) {
      if (candidates[j].type !== 'dot') continue;
      if (candidates[j].num < 10) continue;

      const gap = candidates[j].pos - candidates[i].pos;
      const numDiff = candidates[j].num - candidates[i].num;

      if (numDiff === 1 && gap > 0 && gap < 50) {
        excludedPositions.add(candidates[i].pos);
        excludedPositions.add(candidates[j].pos);
      }
    }
  }

  // 8. 按位置顺序处理候选序号
  let lastMajorNum = 0;
  let lastMinorNum = 0;
  let lastCircleNum = 0;
  let lastChineseNum = 0;
  let lastLetterNum = 0;
  let lastRightParenNum = 0;
  let offset = 0;

  const sortedCandidates = candidates.sort((a, b) => a.pos - b.pos);

  // 通用插入函数：在 pos 前插入 <br>（幂等：若已有 <br> 或 </p> 则跳过）
  const insertBreakBefore = (originalPos: number, raw: string): boolean => {
    const adjustedPos = originalPos + offset;
    const searchStart = Math.max(0, adjustedPos - 10);
    const insertPos = result.indexOf(raw, searchStart);
    if (insertPos < 0) return false;

    // 幂等检查：前 4 字符若已是 <br>，或前 4 字符是 </p>/</div>，则跳过
    const beforeInsert = result.substring(Math.max(0, insertPos - 4), insertPos);
    const beforeInsertLong = result.substring(Math.max(0, insertPos - 6), insertPos);
    if (beforeInsert.endsWith('<br>') ||
        beforeInsertLong.endsWith('</p>') ||
        beforeInsertLong.endsWith('</div>')) {
      return false;
    }

    result = result.substring(0, insertPos) + '<br>' + result.substring(insertPos);
    offset += 4;
    return true;
  };

  for (const candidate of sortedCandidates) {
    if (excludedPositions.has(candidate.pos) || protectedDecimals.has(candidate.pos)) {
      continue;
    }

    switch (candidate.type) {
      case 'dot':
        if (candidate.num > lastMajorNum) {
          if (insertBreakBefore(candidate.pos, `${candidate.num}.`)) {
            lastMajorNum = candidate.num;
            lastMinorNum = 0;
          }
        }
        break;
      case 'paren':
        if (candidate.num > lastMinorNum) {
          if (insertBreakBefore(candidate.pos, `（${candidate.num}）`)) {
            lastMinorNum = candidate.num;
          }
        }
        break;
      case 'rightParen':
        if (candidate.num > lastRightParenNum) {
          if (insertBreakBefore(candidate.pos, `${candidate.num})`)) {
            lastRightParenNum = candidate.num;
          }
        }
        break;
      case 'circle':
        if (candidate.num > lastCircleNum) {
          if (insertBreakBefore(candidate.pos, candidate.raw)) {
            lastCircleNum = candidate.num;
          }
        }
        break;
      case 'chinese':
        if (candidate.num > lastChineseNum) {
          if (insertBreakBefore(candidate.pos, candidate.raw)) {
            lastChineseNum = candidate.num;
          }
        }
        break;
      case 'letter':
        if (candidate.num > lastLetterNum) {
          if (insertBreakBefore(candidate.pos, candidate.raw)) {
            lastLetterNum = candidate.num;
          }
        }
        break;
    }
  }

  // 压缩连续 <br>
  result = result.replace(/(<br\s*\/?>[\s\n\r]*){2,}/gi, '<br>');

  return result;
}

/**
 * 图文分行：文字与图片之间若无换行符，插入 <br>
 * 仅在"文字+图片"紧邻且无任何分隔符时触发
 *
 * 幂等性保证：
 * - 检测到 <br></p></div> 等换行符时不插入
 * - 多次执行结果一致
 */
function splitImageFromText(html: string): string {
  if (!html) return html;
  let result = html;
  if (!result.includes('<img')) return result;

  // 步骤1: 拆分 <p>文字<img></p> 这种图文同段的情况
  // 将 <p>...文字...<img.../>...文字...</p> 拆分为 <p>...文字...</p><br><img.../><br><p>...文字...</p>
  result = result.replace(/<p\b([^>]*)>([\s\S]*?)<\/p>/gi, (match, pAttrs: string, inner: string) => {
    if (!/<img/i.test(inner)) return match; // 段落内无图片，保持原样
    // 段落内有图片：按图片拆分
    const parts: string[] = [];
    let lastIdx = 0;
    const imgRegex = /<img[^>]*>/gi;
    let m: RegExpExecArray | null;
    while ((m = imgRegex.exec(inner)) !== null) {
      const beforeText = inner.substring(lastIdx, m.index);
      if (beforeText.trim()) {
        parts.push(`<p${pAttrs}>${beforeText.trim()}</p>`);
      }
      parts.push(m[0]); // 图片本身
      lastIdx = m.index + m[0].length;
    }
    const afterText = inner.substring(lastIdx);
    if (afterText.trim()) {
      parts.push(`<p${pAttrs}>${afterText.trim()}</p>`);
    }
    // 用 <br> 连接各部分（图片与文字之间分行）
    return parts.join('<br>');
  });

  // 步骤2: 图片前若是文字（非换行符结尾），插入 <br> 分行
  // 匹配：非换行符的字符紧跟 <img，且中间无 <br>/</p>/</div>
  result = result.replace(/([^\s<>](?<!<br>|<\/p>|<\/div>|<p[^>]*>))(\s*)(<img)/gi, (match, before, gap, imgTag) => {
    return `${before}<br>${imgTag}`;
  });

  // 步骤3: 图片后若是文字（非换行符开头），插入 <br> 分行
  // 匹配：<img.../> 后紧跟非换行符的文字
  result = result.replace(/(<img[^>]*>)(\s*)([^\s<])/gi, (match, imgTag, gap, after) => {
    return `${imgTag}<br>${after}`;
  });

  // 步骤4: 清理图文之间多余的空行
  // 4.1 合并连续的 <br>（2个及以上合并为1个）
  result = result.replace(/(<br\s*\/?>[\s\n\r]*){2,}/gi, '<br>');
  // 4.2 清理 <br> 与 </p> 之间的空行：<p></p><br><img => <br><img
  result = result.replace(/<p\b[^>]*>\s*<\/p>\s*(<br\s*\/?>)?\s*(<img)/gi, '$2');
  // 4.3 清理 <img> 与空 <p></p> 之间的空行：<img><br><p></p> => <img>
  result = result.replace(/(<img[^>]*>)\s*(<br\s*\/?>)?\s*<p\b[^>]*>\s*<\/p>/gi, '$1');
  // 4.4 清理 <br> 紧跟在 </p> 后面的情况（</p> 已是块级元素，无需 <br>）
  result = result.replace(/<\/p>\s*<br\s*\/?>/gi, '</p>');
  // 4.5 清理 <br> 紧跟在 <p> 前面的情况
  result = result.replace(/<br\s*\/?>\s*<p\b/gi, '<p');
  // 4.6 再次合并可能产生的连续 <br>
  result = result.replace(/(<br\s*\/?>[\s\n\r]*){2,}/gi, '<br>');

  return result;
}

/**
 * 试卷解析时的完整规范化：图文分行 + 序号分行
 * 用于 Word 解析后、写入数据库前
 *
 * 处理顺序：
 * 1. 序号分行（支持数字/字母/中文/圆圈多种格式）
 * 2. 图文分行（文字与图片之间插入 <br>）
 *
 * 幂等性：多次执行结果一致，不会重复插入 <br>
 */
/**
 * 给所有 <p> 标签注入 line-height: 1em 样式
 * - 已有 style 属性：合并 line-height（覆盖已有值）
 * - 无 style 属性：新增 style="line-height: 1em"
 * - 幂等：重复执行不会叠加
 */
function applyParagraphLineHeight(html: string): string {
  if (!html) return html;
  // 匹配所有 <p> 开标签（含属性），统一注入 line-height: 1em
  return html.replace(/<p\b([^>]*)>/gi, (match, attrs: string) => {
    // 移除已有的 line-height 声明（避免重复）
    const cleanedAttrs = attrs
      .replace(/\s*style\s*=\s*"[^"]*"/gi, (styleMatch: string) => {
        // 从 style 内容中移除 line-height 声明
        return styleMatch.replace(/line-height\s*:\s*[^;"]+;?\s*/gi, '');
      })
      .replace(/\s*style\s*=\s*'[^']*'/gi, (styleMatch: string) => {
        return styleMatch.replace(/line-height\s*:\s*[^;']+\s*/gi, '');
      });

    // 判断清理后是否还有 style 属性
    const hasStyle = /\bstyle\s*=\s*["']/.test(cleanedAttrs);
    if (hasStyle) {
      // 在已有的 style="..." 中追加 line-height
      return `<p${cleanedAttrs.replace(/(style\s*=\s*["'])/i, '$1line-height: 1em; ')}>`;
    }
    return `<p style="line-height: 1em;"${cleanedAttrs}>`;
  });
}

export function normalizePaperHtml(html: string): string {
  if (!html) return html;
  let result = html;
  // 1. 序号分行（扩展版，支持数字/字母/中文序号）
  result = autoSplitNumbers(result);
  // 2. 图文分行（仅在文字与图片紧邻且无换行符时插入 <br>）
  result = splitImageFromText(result);
  // 3. 统一段落行距为 1em（约 1 倍字号），入库前固化
  result = applyParagraphLineHeight(result);
  return result;
}
